/**
 * @copyright Microsoft Corporation. All rights reserved.
 */
const flightsSplitter = ';';
const keyValueFlightSplitter = ':';
const flightTokenSplitter = '.';
export function parseFlights(serializedFlights) {
    if (!serializedFlights) {
        return new WorkflowFlights([], new Set(), new Map(), new Map());
    }
    const flights = serializedFlights.split(flightsSplitter);
    const normalizedFlights = new Set();
    const flightsMap = new Map();
    // The full flight names from ECS/EXP are different across apps/platforms, e.g.,
    // Microsoft.Office.[App].[Augloop?].[flight]:[value] or just [flight]:[value]
    // When enabling a flight globally, it'd be handy to map {[flight] : full flight name},
    // so that runtime-client would just need to query by [flight].
    const parsedFlightsMap = new Map();
    for (const flight of flights) {
        const trimmedFlight = flight.trim();
        if (trimmedFlight === '') {
            continue;
        }
        normalizedFlights.add(WorkflowFlights.normalizeName(trimmedFlight));
        // if a flight comprises of the separator, then only the first part of the flight is picked
        const [flightName, ...flightValueParts] = trimmedFlight.split(keyValueFlightSplitter);
        const flightValue = flightValueParts.join(keyValueFlightSplitter);
        const normalizedFlightName = WorkflowFlights.normalizeName(flightName);
        if (normalizedFlightName) {
            flightsMap.set(normalizedFlightName, flightValue);
            const parsedFlightName = WorkflowFlights.parseName(normalizedFlightName);
            if (parsedFlightName && parsedFlightName !== normalizedFlightName) {
                parsedFlightsMap.set(parsedFlightName, normalizedFlightName);
            }
        }
    }
    return new WorkflowFlights(flights, normalizedFlights, flightsMap, parsedFlightsMap);
}
// For each flight name, remove the duplicate values and only retain the last value
export function removeDuplicateFlights(flights) {
    if (!flights) {
        return '';
    }
    const flightNameSet = new Set();
    const uniqueFlights = [];
    // Iterate in reverse order to retain the last value of the flight
    for (const flight of flights.split(flightsSplitter).reverse()) {
        const flightName = flight.split(keyValueFlightSplitter)[0];
        const normalizedFlightName = WorkflowFlights.normalizeName(flightName);
        // If the flight name is already present, skip it
        if (!flightNameSet.has(normalizedFlightName)) {
            flightNameSet.add(normalizedFlightName);
            uniqueFlights.push(flight);
        }
    }
    // Reverse the uniqueFlights to get the original order
    return uniqueFlights.reverse().join(flightsSplitter);
}
export class WorkflowFlights {
    constructor(originalFlights, normalizedFlights, keyValueFlightsMap, parsedFlightsMap) {
        this.originalFlights = originalFlights;
        this.normalizedFlights = normalizedFlights;
        this.keyValueFlightsMap = keyValueFlightsMap;
        this.parsedFlightsMap = parsedFlightsMap;
    }
    static normalizeName(name) {
        return name === null || name === void 0 ? void 0 : name.toLowerCase();
    }
    static parseName(name) {
        const tokens = name === null || name === void 0 ? void 0 : name.split(flightTokenSplitter);
        return tokens ? tokens[tokens.length - 1] : undefined;
    }
    hasFlight(flight) {
        const normalizedName = WorkflowFlights.normalizeName(flight);
        const wholeFlightMatched = this.normalizedFlights.has(normalizedName);
        const flightNameMatched = this.keyValueFlightsMap.has(normalizedName);
        const parsedFlightNameMatched = this.parsedFlightsMap.has(normalizedName);
        return wholeFlightMatched || flightNameMatched || parsedFlightNameMatched;
    }
    getBooleanValue(flightName, fallbackValue) {
        var _a, _b;
        // Honor eiter form of flight name
        const flightValue = (_a = this.getStringValue(flightName)) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        const parsedFlightValue = (_b = this.getStringValue(this.parsedFlightsMap.get(WorkflowFlights.normalizeName(flightName)))) === null || _b === void 0 ? void 0 : _b.toLowerCase();
        if (flightValue === 'true' || parsedFlightValue === 'true') {
            return true;
        }
        else if (flightValue === 'false' || parsedFlightValue === 'false') {
            return false;
        }
        else {
            return fallbackValue;
        }
    }
    getIntValue(flightName, fallbackValue) {
        const value = this.getStringValue(flightName);
        const parsed = Number.parseInt(value, 10);
        if (Number.isNaN(parsed)) {
            return fallbackValue;
        }
        return parsed;
    }
    getStringValue(flightName, fallbackValue) {
        var _a;
        return (_a = this.keyValueFlightsMap.get(WorkflowFlights.normalizeName(flightName))) !== null && _a !== void 0 ? _a : fallbackValue;
    }
    getAll() {
        return this.originalFlights;
    }
    getAllParsed() {
        const result = [];
        this.keyValueFlightsMap.forEach((value, name) => {
            const normalizedValue = value === null || value === void 0 ? void 0 : value.toLowerCase();
            switch (normalizedValue) {
                case 'true':
                    result.push({ name, value: true });
                    break;
                case 'false':
                    result.push({ name, value: false });
                    break;
                default:
                    { // gotta have the {}'s to have block scope const parsed
                        const parsed = Number.parseInt(normalizedValue, 10);
                        if (Number.isNaN(parsed)) {
                            // preserve letter case of the original value
                            result.push({ name, value: value });
                        }
                        else {
                            result.push({ name, value: parsed });
                        }
                    }
                    break;
            }
        });
        return result;
    }
}
//# sourceMappingURL=workflow-flights.js.map